import { memo, type MouseEvent, type ReactNode } from "react"
import { CollectionSummary } from "@/types/collection"
import pluralize from "pluralize"

import { relativeCanonicalUrl } from "@/lib/entity-utils"
import IconLock from "@/components/icons/lock"
import { AddToMediaButton } from "./add-to-media-button"
import { AvatarUserGroup } from "./avatar-group"
import { CollectionImagesGrid } from "./collection-images-grid"
import { SaveCollectionButton } from "./save-entity-button"
import { IconButton } from "./ui/icon-button"
import { Link } from "./ui/link"
import { Skeleton } from "./ui/skeleton"

const CARD_CLASSES = {
  root: "group @container/collection-card",
  inner:
    "contents [--pad:theme(space[2.5])] @[14rem]/collection-card:[--pad:theme(space.4)]",
}

interface CollectionCardProps {
  openInNewTab?: boolean
  onClick?: (e: MouseEvent) => void
  collection?: CollectionSummary
  showPrivateBadge?: boolean
  renderActions?: ReactNode // null renders no actions; undefined renders the save and add-to-media buttons
  showOwnerAvatar?: boolean // show the owner's avatar when the collection has no collaborators
}

function CollectionCardContent({
  openInNewTab = false,
  onClick,
  collection,
  showPrivateBadge = true,
  renderActions,
  showOwnerAvatar = false,
}: CollectionCardProps & { collection: CollectionSummary }) {
  const collaborators = collection.accepted_collaborator_refs

  return (
    <div className={CARD_CLASSES.root}>
      <div className={CARD_CLASSES.inner}>
        <div className="relative overflow-hidden rounded-2xl">
          <Link
            href={relativeCanonicalUrl(collection.canonical_url)}
            aria-label={collection.name}
            className="flex aspect-almost-square items-center justify-center overflow-hidden rounded-[inherit]"
            target={openInNewTab ? "_blank" : undefined}
            onClick={onClick}
          >
            <CollectionImagesGrid images={collection.images} />
            {!collection.public && showPrivateBadge && (
              <div className="absolute top-3 left-3">
                <IconButton
                  variant="dark-overlay"
                  icon={IconLock}
                  label="Private"
                  size="xs"
                  className="cursor-default"
                />
              </div>
            )}
          </Link>
          <div className="absolute top-(--pad) right-(--pad) flex items-center gap-2">
            {renderActions !== undefined ? (
              renderActions
            ) : (
              <>
                <SaveCollectionButton
                  collection={collection}
                  trackingContext="CollectionCard"
                  tooltipSide="top"
                  tooltipOffset={4}
                />
                <AddToMediaButton
                  dest={collection}
                  trackingContext="CollectionCard"
                  tooltipSide="top"
                  tooltipOffset={4}
                />
              </>
            )}
          </div>
        </div>
        <Link
          href={relativeCanonicalUrl(collection.canonical_url)}
          onClick={onClick}
          className="flex flex-col gap-0.5 pt-(--pad) text-xs leading-tight @[14rem]/collection-card:text-sm @[21.375rem]/collection-card:text-base"
        >
          <div className="flex items-start gap-2">
            <div className="no-translate line-clamp-2 flex-1 leading-tight font-semibold text-pretty wrap-anywhere">
              {collection.name}
            </div>
            {collaborators ? (
              <AvatarUserGroup
                userRefs={collaborators}
                size="2xs"
                className="-mt-0.5"
                tooltip
                maxShown={2}
              />
            ) : (
              showOwnerAvatar && (
                <AvatarUserGroup
                  users={[collection.owner]}
                  size="2xs"
                  className="-mt-0.5"
                  tooltip
                />
              )
            )}
          </div>
          <div className="flex items-center gap-1 text-[.9375em] text-muted">
            {pluralize("place", collection.favorites_count, true)}
          </div>
        </Link>
      </div>
    </div>
  )
}

export const CollectionCard = memo(function CollectionCard({
  collection,
  skeletonContent,
  ...props
}: CollectionCardProps & {
  skeletonContent?: ReactNode
}) {
  if (!collection) {
    return (
      <div className={CARD_CLASSES.root}>
        <div className={CARD_CLASSES.inner}>
          <div className="relative">
            <Skeleton className="aspect-almost-square" />
            {skeletonContent}
          </div>
          <div className="flex flex-col gap-1.5 pt-(--pad)">
            <Skeleton className="h-[1em] w-10/12" />
            <Skeleton className="h-[1em] w-7/12" />
          </div>
        </div>
      </div>
    )
  }

  return <CollectionCardContent collection={collection} {...props} />
})
